import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';
import {
  Card, StatGrid, Stat, StatTop, StatIco, StatLabel, StatValue,
  ChartGrid, Tag, TagDot, Btn, Chip, MiniProg,
  CellPrimary, Act, Av, Muted, Field, KpiIco,
} from '../ui/primitives';
import DataTable from '../ui/DataTable';
import { Icon, ModalShell } from '../ui/shell';
import { fetchCourseAnalysis, regenerateCourseAnalysis } from './courseAnalysisApi';
import {
  COURSE_ANALYSIS_LIST_DEFAULTS,
  COURSE_ANALYSIS_SORT_FIELDS,
  getNextCourseAnalysisSort,
  sortCourseAnalysisLocally,
} from './courseAnalysisListApi';
import {
  COURSE_ANALYSIS_MESSAGES,
  getCourseAnalysisFetchErrorMessage,
} from './courseAnalysisMessages';
import { COURSE_ANALYSIS_FIELD_HELP } from './courseAnalysisFieldHelp';
import { LabelWithHelp } from '../ui/FieldHelp';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

/* ============================================================
   Course Analysis tab — data from GET/POST /api/course/{id}/analysis only.
   No mock or fallback insight banks. Empty sections show real empty states.
   ============================================================ */

const caShimmer = keyframes`
  0% { background-position: 0% 0; }
  100% { background-position: -200% 0; }
`;

const Wrap = styled.div``;

const CaCard = styled(Card)`
  background: ${({ theme }) => theme.surface};
`;

const CaHero = styled(CaCard)`
  padding: 22px 24px;
  position: relative;
  overflow: hidden;
  border-left: 3px solid ${({ theme }) => theme.accent};
  background: ${({ theme }) => `
    radial-gradient(700px 200px at -10% -50%, color-mix(in oklch, ${theme.accent} 13%, transparent), transparent 60%),
    ${theme.surface}
  `};
`;

const CaHeroTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
`;

const CaAv = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ theme }) => `linear-gradient(135deg, ${theme.accent}, color-mix(in oklch, ${theme.accent} 60%, #000))`};
  color: #fff;
  display: grid;
  place-items: center;
  font-family: ${({ theme }) => theme.fontHead};
  font-weight: 800;
  font-size: 17px;
  box-shadow: 0 2px 6px color-mix(in oklch, ${({ theme }) => theme.accent} 30%, transparent);
  flex: none;
`;

const CaHeroTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  font-family: ${({ theme }) => theme.fontHead};
  font-weight: 700;
  font-size: 17px;
  color: ${({ theme }) => theme.text};
  letter-spacing: -.01em;
`;

const CaBy = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.accentStrong};
  background: ${({ theme }) => theme.accentSoft};
  padding: 3px 8px;
  border-radius: 999px;
`;

const CaHeroMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 4px;
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textMuted};
`;

const CaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${({ theme }) => theme.textSubtle};
`;

const CaNarr = styled.p`
  margin: 18px 0 2px;
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 17px;
  line-height: 1.55;
  color: ${({ theme }) => theme.text};
  max-width: 78ch;
`;

const CaNarrLoading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-top: 18px;
`;

const CaSkel = styled.span`
  display: block;
  height: 14px;
  border-radius: 6px;
  width: ${({ $w }) => $w || '100%'};
  background: linear-gradient(90deg, ${({ theme }) => theme.surface3}, ${({ theme }) => theme.surface2}, ${({ theme }) => theme.surface3});
  background-size: 200% 100%;
  animation: ${caShimmer} 1.1s infinite ease-in-out;
`;

const CaSecH = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  font-family: ${({ theme }) => theme.fontHead};
  font-weight: 700;
  font-size: 15px;
  color: ${({ theme }) => theme.text};

  > div:first-child {
    display: flex;
    align-items: center;
    gap: 8px;
    svg { color: ${({ theme }) => theme.accentStrong}; }
  }
`;

const CaPad = styled(CaCard)`
  padding: ${({ theme }) => theme.padCard};
`;

const CaGap = styled.div`
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding: 12px 14px;
  background: ${({ theme }) => theme.surface2};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  cursor: pointer;
  transition: border-color .12s, background .12s;

  &:hover {
    border-color: ${({ theme }) => theme.borderStrong};
    background: ${({ theme }) => theme.surface};
  }
`;

const CaGapRail = styled.div`
  width: 3px;
  border-radius: 2px;
  align-self: stretch;
  flex: none;
  background: ${({ $color }) => $color};
`;

const CaRiskRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px dashed ${({ theme }) => theme.border};

  &:last-of-type { border-bottom: none; }
`;

const CaEmpty = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 4px;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.text};
  line-height: 1.45;
`;

const SectionEmpty = styled.div`
  padding: 20px 12px;
  text-align: center;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const CaRec = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.surface2};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  transition: background .15s, border-color .15s;
  opacity: ${({ $applied }) => ($applied ? 0.65 : 1)};

  &:hover { border-color: ${({ theme }) => theme.borderStrong}; }
`;

const CaRecIco = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex: none;
  display: grid;
  place-items: center;
  background: ${({ theme }) => theme.accentSoft};
  color: ${({ theme }) => theme.accentStrong};
`;

const CaRecActs = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: none;
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  box-shadow: ${({ theme }) => theme.shadowLg};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.text};
  animation: fadeIn .2s ease;
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } }
`;

const StatePanel = styled(CaPad)`
  text-align: center;
  padding: 48px 24px;

  h3 {
    margin: 12px 0 6px;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 16px;
    font-weight: 700;
  }
`;

const CaFmt = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ theme, $on }) => ($on ? theme.accent : theme.border)};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme, $on }) => ($on ? theme.accentSoft : theme.surface)};
  text-align: left;
  cursor: pointer;
  transition: border-color .12s, background .12s;

  &:hover { border-color: ${({ theme }) => theme.borderStrong}; }
`;

const CaGapSummary = styled.div`
  background: ${({ theme }) => theme.surface2};
  border: 1px solid ${({ theme }) => theme.border};
  border-left: 3px solid ${({ $color }) => $color};
  border-radius: ${({ theme }) => theme.rMd};
  padding: 14px 16px;
  font-size: ${({ theme }) => theme.fsSm};
  line-height: 1.5;
`;

const CaQBody = styled.div`
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 16px;
  line-height: 1.55;
  padding: 14px 16px;
  background: ${({ theme }) => theme.surface2};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
`;

const CaOpt = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px 10px 10px;
  border: 1px solid ${({ theme, $correct }) => ($correct ? `color-mix(in oklch, ${theme.success} 35%, ${theme.border})` : theme.border)};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme, $correct }) => ($correct ? `color-mix(in oklch, ${theme.success} 6%, ${theme.surface})` : theme.surface)};
`;

const sevTag = {
  high: ['red', 'High impact'],
  med: ['amber', 'Watch'],
  low: ['gray', 'Low impact'],
};

const sevColor = (severity, theme) => {
  if (severity === 'high') return theme.danger;
  if (severity === 'med') return theme.warning || 'oklch(0.58 0.13 60)';
  return theme.textSubtle;
};

const initials = (name) => String(name || '?').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const mapApiToInsights = (data) => ({
  gaps: data.gaps || [],
  recs: (data.recommendations || []).map((r) => ({
    ic: r.icon || 'sparkles',
    title: r.title,
    body: r.body,
    action: r.action,
  })),
  flags: (data.flaggedQuestions || []).map((f) => ({
    ...f,
    q: f.text || f.q,
    diff: f.difficulty || f.diff,
    acc: f.accuracy ?? f.acc,
    disc: f.discrimination || f.disc,
  })),
});

const formatStamp = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  return d.toLocaleString();
};

function HealthCard({ label, value, delta, tone, icon, sub, help }) {
  const theme = useTheme();
  return (
    <Stat>
      <StatTop>
        <StatIco><Icon name={icon} /></StatIco>
        <Tag $tone={tone === 'green' ? 'green' : tone === 'red' ? 'red' : tone === 'amber' ? 'amber' : 'gray'} style={{ fontSize: 10 }}>
          <TagDot />{delta}
        </Tag>
      </StatTop>
      <StatLabel>
        <LabelWithHelp help={help}>{label}</LabelWithHelp>
      </StatLabel>
      <StatValue className="tnum">{value}</StatValue>
      <Muted style={{ fontSize: theme.fsMicro, marginTop: 4, display: 'block' }}>{sub}</Muted>
    </Stat>
  );
}

function ExportAnalysisModal({ courseName, onClose }) {
  const [fmt, setFmt] = useState('pdf');
  const [includes, setIncludes] = useState({ narrative: true, gaps: true, risk: true, recs: true, diag: true });
  const formats = [
    { id: 'pdf', ic: 'doc', title: 'PDF report', sub: 'Branded report following brand.md, ready to share' },
    { id: 'csv', ic: 'download', title: 'CSV data', sub: 'Raw scores, gaps, learners — for spreadsheet analysis' },
    { id: 'link', ic: 'send', title: 'Shareable link', sub: 'Private, view-only link for co-instructors and admins' },
  ];
  const opts = [
    ['narrative', "Yaki's narrative summary", COURSE_ANALYSIS_FIELD_HELP.includeNarrative],
    ['gaps', 'Knowledge gaps', COURSE_ANALYSIS_FIELD_HELP.includeGaps],
    ['risk', 'Students needing attention', COURSE_ANALYSIS_FIELD_HELP.includeRisk],
    ['recs', 'Recommendations', COURSE_ANALYSIS_FIELD_HELP.includeRecs],
    ['diag', 'Question diagnostics', COURSE_ANALYSIS_FIELD_HELP.includeDiag],
  ];

  return (
    <ModalShell
      title="Export course analysis"
      sub={`${courseName} · generated by Yaki`}
      onClose={onClose}
      max={620}
      foot={
        <>
          <Btn $variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn disabled title={COURSE_ANALYSIS_MESSAGES.EXPORT_COMING_SOON}>
            <Icon name="download" size={16} />Export
          </Btn>
        </>
      }
    >
      <Muted style={{ display: 'block', marginBottom: 16, fontSize: 13, lineHeight: 1.5 }}>
        {COURSE_ANALYSIS_MESSAGES.EXPORT_PREVIEW_HINT}
      </Muted>
      <Field>
        <label>
          <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.format}>Format</LabelWithHelp>
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {formats.map((f) => (
            <CaFmt key={f.id} type="button" $on={fmt === f.id} onClick={() => setFmt(f.id)}>
              <CaRecIco><Icon name={f.ic} size={16} /></CaRecIco>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 650, fontSize: 14 }}>{f.title}</div>
                <Muted style={{ fontSize: 11, marginTop: 2, display: 'block' }}>{f.sub}</Muted>
              </div>
            </CaFmt>
          ))}
        </div>
      </Field>
      <Field style={{ marginBottom: 0 }}>
        <label>Include</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {opts.map(([k, l, help]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={includes[k]} onChange={(e) => setIncludes({ ...includes, [k]: e.target.checked })} />
              <span><LabelWithHelp help={help}>{l}</LabelWithHelp></span>
            </label>
          ))}
        </div>
      </Field>
    </ModalShell>
  );
}

function GapDetailModal({ gap, onClose, onOpenQuestion }) {
  const theme = useTheme();
  const color = sevColor(gap.severity, theme);
  const itemCount = (gap.items && gap.items.length) || 0;
  return (
    <ModalShell
      title={gap.topic}
      sub={`Knowledge gap · ${gap.missRate}% miss rate across ${itemCount} linked item${itemCount === 1 ? '' : 's'}`}
      onClose={onClose}
      max={680}
      foot={<Btn $variant="ghost" onClick={onClose}>Close</Btn>}
    >
      <CaGapSummary $color={color}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Icon name="target" size={15} style={{ color }} />
          <b style={{ fontSize: 13 }}>Why Yaki flagged this</b>
        </div>
        <div>
          {gap.missRate}% of the cohort missed at least one item on this topic. Review linked questions and consider a short remediation module.
        </div>
      </CaGapSummary>
      {itemCount > 0 && (
        <>
          <CaSecH style={{ marginTop: 18 }}>
            <div><Icon name="assess" size={15} /> Linked questions</div>
          </CaSecH>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {gap.items.map((qid) => {
              const desc = gap.itemDescs ? gap.itemDescs[qid] : '';
              return (
                <div key={qid} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: theme.surface2, border: `1px solid ${theme.border}`, borderRadius: theme.rMd }}>
                  <Chip $soft style={{ fontWeight: 700 }}>{qid}</Chip>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <CellPrimary style={{ fontSize: 13 }}>{desc || 'Linked item'}</CellPrimary>
                  </div>
                  <Btn $variant="ghost" $sm onClick={() => onOpenQuestion(qid)}>View</Btn>
                </div>
              );
            })}
          </div>
        </>
      )}
    </ModalShell>
  );
}

function QuestionDetailModal({ flag, onClose, onRewrite }) {
  const theme = useTheme();
  const displayText = flag.text || flag.q;
  const options = flag.options || flag.opts || [];
  const hasRewrite = flag.rewrite != null;
  return (
    <ModalShell
      title={displayText || 'Question detail'}
      sub={`${flag.difficulty || flag.diff} · ${flag.accuracy ?? flag.acc ?? '—'}% accuracy · discrimination ${flag.discrimination || flag.disc || '—'}`}
      onClose={onClose}
      max={720}
      foot={
        <>
          <Btn $variant="ghost" onClick={onClose}>Close</Btn>
          {hasRewrite && (
            <Btn onClick={onRewrite}><Icon name="sparkles" size={16} />Rewrite with Yaki</Btn>
          )}
        </>
      }
    >
      {displayText ? (
        <>
          <CaQBody>{displayText}</CaQBody>
          {options.length > 0 && (
            <>
              <CaSecH style={{ marginTop: 16 }}>
                <div><Icon name="users" size={15} /> Cohort response distribution</div>
              </CaSecH>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {options.map((o, i) => {
                  const correct = o.correct === true;
                  const picked = o.picked || 0;
                  return (
                    <CaOpt key={i} $correct={correct}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 7, flex: 'none', display: 'grid', placeItems: 'center',
                        background: correct ? theme.success : theme.surface3,
                        color: correct ? '#fff' : theme.textMuted,
                        fontWeight: 700, fontSize: 12,
                      }}>
                        {String.fromCharCode(65 + i)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {o.label}
                          {correct && <Tag $tone="green" style={{ fontSize: 10 }}>Correct</Tag>}
                        </div>
                        <div style={{ marginTop: 6, height: 4, borderRadius: 3, background: theme.surface3, overflow: 'hidden' }}>
                          <div style={{ width: `${picked}%`, height: '100%', background: correct ? theme.success : theme.accent, borderRadius: 3 }} />
                        </div>
                      </div>
                      <span className="tnum" style={{ fontWeight: 700, fontSize: 12, color: theme.textMuted, minWidth: 36, textAlign: 'right' }}>{picked}%</span>
                    </CaOpt>
                  );
                })}
              </div>
            </>
          )}
          {flag.rationale && (
            <div style={{ marginTop: 16, padding: '14px 16px', background: theme.accentSoft, borderRadius: theme.rMd, borderLeft: `3px solid ${theme.accent}`, fontSize: theme.fsSm, lineHeight: 1.55 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontSize: 12, color: theme.accentStrong, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
                <Icon name="sparkles" size={13} /> Yaki&apos;s rationale
              </div>
              {flag.rationale}
            </div>
          )}
        </>
      ) : (
        <Muted>{COURSE_ANALYSIS_MESSAGES.QUESTION_DETAIL_EMPTY}</Muted>
      )}
    </ModalShell>
  );
}

function RewriteModal({ flag, onClose }) {
  const theme = useTheme();
  const data = flag.rewrite;
  if (!data) return null;
  const originalText = flag.text || flag.q || '';
  return (
    <ModalShell
      title="Yaki's rewrite"
      sub={originalText}
      onClose={onClose}
      max={800}
      foot={<Btn $variant="ghost" onClick={onClose}>Close</Btn>}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <Muted style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', marginBottom: 8 }}>Original</Muted>
          <CaQBody style={{ fontSize: 14 }}>{originalText}</CaQBody>
        </div>
        <div>
          <Muted style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', display: 'block', marginBottom: 8, color: theme.accentStrong }}>Yaki&apos;s version</Muted>
          <CaQBody style={{ fontSize: 14, borderColor: theme.accent }}>{data.text}</CaQBody>
        </div>
      </div>
      {data.changes && data.changes.length > 0 && (
        <>
          <CaSecH style={{ marginTop: 18 }}>
            <div><Icon name="sparkles" size={15} /> What Yaki changed</div>
          </CaSecH>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: theme.fsSm, lineHeight: 1.6 }}>
            {data.changes.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </>
      )}
      {data.whyItHelps && (
        <div style={{ marginTop: 14, fontSize: theme.fsSm, lineHeight: 1.55, color: theme.textMuted }}>
          {data.whyItHelps}
        </div>
      )}
    </ModalShell>
  );
}

function AnalysisShimmer() {
  return (
    <CaPad>
      <CaNarrLoading>
        <CaSkel $w="92%" />
        <CaSkel $w="85%" />
        <CaSkel $w="58%" />
      </CaNarrLoading>
    </CaPad>
  );
}

export default function CourseAnalysis({
  courseId,
  course,
  enrollees = [],
  assessments = [],
}) {
  const theme = useTheme();
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const courseName = course.courseName || course.name || 'Course';

  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [gapDetail, setGapDetail] = useState(null);
  const [qDetail, setQDetail] = useState(null);
  const [rewriting, setRewriting] = useState(null);
  const [diagSortBy, setDiagSortBy] = useState(COURSE_ANALYSIS_LIST_DEFAULTS.sortBy);
  const [diagSortDir, setDiagSortDir] = useState(COURSE_ANALYSIS_LIST_DEFAULTS.sortDir);
  // Needs-attention list is collapsed to the first few; toggles to full list.
  const [showAllRisk, setShowAllRisk] = useState(false);

  const insightsFlags = useMemo(() => {
    if (!analysisData?.sufficientData) return [];
    return mapApiToInsights(analysisData).flags;
  }, [analysisData]);

  const sortedFlags = useMemo(
    () => sortCourseAnalysisLocally(insightsFlags, diagSortBy, diagSortDir),
    [insightsFlags, diagSortBy, diagSortDir],
  );

  const handleDiagSortChange = (columnKey) => {
    const next = getNextCourseAnalysisSort(columnKey, diagSortBy, diagSortDir);
    setDiagSortBy(next.sortBy);
    setDiagSortDir(next.sortDir);
  };

  const loadAnalysis = useCallback(async () => {
    if (!courseId) {
      setLoading(false);
      setFetchError('No course selected.');
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const data = await fetchCourseAnalysis(courseId);
      setAnalysisData(data);
    } catch (err) {
      console.error('Course analysis fetch failed:', err);
      setAnalysisData(null);
      setFetchError(getCourseAnalysisFetchErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(id);
  }, [toast]);

  const regenerate = async () => {
    if (!courseId || generating) return;
    if (!guardAction('action.yaki.analysis.regenerate')) return;
    setGenerating(true);
    try {
      const data = await regenerateCourseAnalysis(courseId);
      setAnalysisData(data);
      setFetchError(null);
      setToast('Analysis refreshed');
    } catch (err) {
      console.error('Regenerate analysis failed:', err);
      setToast(COURSE_ANALYSIS_MESSAGES.REGENERATE_FAILED);
    } finally {
      setGenerating(false);
    }
  };

  if (!courseId) {
    return (
      <StatePanel>
        <h3>No course selected</h3>
        <Muted style={{ display: 'block' }}>Open a course to view its analysis.</Muted>
      </StatePanel>
    );
  }

  if (loading) {
    return <AnalysisShimmer />;
  }

  if (fetchError || !analysisData) {
    return (
      <StatePanel>
        <KpiIco style={{ margin: '0 auto', background: theme.accentSoft, color: theme.accentStrong }}>
          <Icon name="flag" />
        </KpiIco>
        <h3>{COURSE_ANALYSIS_MESSAGES.TITLE_UNAVAILABLE}</h3>
        <Muted style={{ display: 'block', maxWidth: 420, margin: '0 auto', lineHeight: 1.55 }}>
          {fetchError || COURSE_ANALYSIS_MESSAGES.EMPTY_DATA}
        </Muted>
        <Btn style={{ marginTop: 16 }} onClick={loadAnalysis}>
          <Icon name="sparkles" size={16} />Retry
        </Btn>
      </StatePanel>
    );
  }

  if (!analysisData.sufficientData) {
    return (
      <StatePanel>
        <KpiIco style={{ margin: '0 auto', background: theme.accentSoft, color: theme.accentStrong }}>
          <Icon name="sparkles" />
        </KpiIco>
        <h3>Not enough data yet</h3>
        <Muted style={{ display: 'block', maxWidth: 420, margin: '0 auto', lineHeight: 1.55 }}>
          Yaki needs about 5 enrollees or 5 graded submissions before a meaningful analysis can be generated. Invite learners and wait for graded attempts.
        </Muted>
        <Btn $variant="ghost" $sm style={{ marginTop: 16 }} onClick={regenerate} disabled={generating}>
          <Icon name={generating ? 'clock' : 'sparkles'} size={15} />
          {generating ? 'Checking…' : 'Check again'}
        </Btn>
      </StatePanel>
    );
  }

  const insights = mapApiToInsights(analysisData);
  const metrics = analysisData.metrics || {};
  const engagement = metrics.engagement ?? 0;
  const pacing = metrics.pacing ?? 0;
  const mastery = metrics.mastery ?? 0;
  const atRisk = analysisData.atRiskLearners || [];
  const stamp = formatStamp(analysisData.generatedAt);
  const narrative = analysisData.narrative;

  const findFlagById = (qid) => insights.flags.find((f) => f.id === qid);

  return (
    <Wrap className="route-enter">
      <CaHero>
        <CaHeroTop>
          <CaAv>Y</CaAv>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CaHeroTitle>
              <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.courseAnalysis}>Course analysis</LabelWithHelp>
              <CaBy>by Yaki</CaBy>
            </CaHeroTitle>
            <CaHeroMeta>
              <Icon name="check" size={12} />
              Generated {stamp}
              <CaDot />
              {enrollees.length} enrollees · {assessments.length} assessment{assessments.length === 1 ? '' : 's'}
            </CaHeroMeta>
          </div>
          <Btn $variant="ghost" $sm onClick={regenerate} disabled={generating}>
            <Icon name={generating ? 'clock' : 'sparkles'} size={15} />
            {generating ? 'Analyzing…' : 'Regenerate'}
          </Btn>
          <Btn $variant="ghost" $sm onClick={() => guardAction('action.yaki.analysis.export', () => setExporting(true))}>
            <Icon name="download" size={15} />Export
          </Btn>
        </CaHeroTop>
        {generating ? (
          <CaNarrLoading>
            <CaSkel $w="92%" />
            <CaSkel $w="85%" />
            <CaSkel $w="58%" />
          </CaNarrLoading>
        ) : narrative ? (
          <CaNarr>{narrative}</CaNarr>
        ) : (
          <Muted style={{ display: 'block', marginTop: 18, fontSize: theme.fsSm }}>
            No narrative summary in this report yet. Regenerate after more submissions.
          </Muted>
        )}
      </CaHero>

      <StatGrid style={{ marginTop: theme.gap }}>
        <HealthCard
          label="Engagement"
          value={`${engagement}%`}
          delta={engagement >= 70 ? 'Strong' : engagement >= 50 ? 'Moderate' : 'Low'}
          tone={engagement >= 70 ? 'green' : engagement >= 50 ? 'amber' : 'red'}
          icon="enroll"
          sub="avg progress across cohort"
          help={COURSE_ANALYSIS_FIELD_HELP.engagement}
        />
        <HealthCard
          label="Mastery"
          value={`${mastery}%`}
          delta={mastery >= 75 ? 'On target' : 'Below target'}
          tone={mastery >= 75 ? 'green' : mastery >= 60 ? 'amber' : 'red'}
          icon="award"
          sub="weighted score on graded items"
          help={COURSE_ANALYSIS_FIELD_HELP.mastery}
        />
        <HealthCard
          label="Pacing"
          value={`${pacing}%`}
          delta={pacing >= 70 ? 'On schedule' : 'Behind schedule'}
          tone={pacing >= 70 ? 'green' : 'amber'}
          icon="clock"
          sub="against course timeline"
          help={COURSE_ANALYSIS_FIELD_HELP.pacing}
        />
        <HealthCard
          label="At-risk"
          value={atRisk.length}
          delta={atRisk.length ? 'needs outreach' : 'all clear'}
          tone={atRisk.length > 2 ? 'red' : atRisk.length > 0 ? 'amber' : 'green'}
          icon="flag"
          sub="learners flagged by Yaki"
          help={COURSE_ANALYSIS_FIELD_HELP.atRisk}
        />
      </StatGrid>

      <ChartGrid style={{ gridTemplateColumns: '1.3fr 1fr', marginTop: theme.space }}>
        <CaPad>
          <CaSecH>
            <div>
              <Icon name="target" size={16} />
              <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.knowledgeGaps}>Knowledge gaps detected</LabelWithHelp>
            </div>
            <Muted style={{ fontSize: theme.fsMicro }}>{insights.gaps.length} gaps · ranked by impact</Muted>
          </CaSecH>
          {insights.gaps.length === 0 ? (
            <SectionEmpty>No knowledge gaps detected for this course yet.</SectionEmpty>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {insights.gaps.map((g, i) => {
                const [tone, lbl] = sevTag[g.severity] || sevTag.med;
                const railColor = sevColor(g.severity, theme);
                const items = g.items || [];
                return (
                  <CaGap key={i} onClick={() => setGapDetail(g)}>
                    <CaGapRail $color={railColor} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
                        <CellPrimary style={{ fontSize: 14 }}>{g.topic}</CellPrimary>
                        <Tag $tone={tone}><TagDot />{lbl}</Tag>
                      </div>
                      <Muted style={{ fontSize: theme.fsMicro, marginTop: 5, display: 'block' }}>
                        {g.missRate}% of cohort missed this
                        {items.length > 0 && ` · linked to ${items.map((id) => `${id}${g.itemDescs && g.itemDescs[id] ? ` · ${g.itemDescs[id]}` : ''}`).join(', ')}`}
                      </Muted>
                      <MiniProg style={{ marginTop: 8 }}>
                        <div className="track" style={{ width: 220 }}>
                          <i style={{ width: `${g.missRate}%`, background: railColor }} />
                        </div>
                        <span className="tnum">{g.missRate}%</span>
                      </MiniProg>
                    </div>
                    <Act title="Open linked questions" onClick={(e) => { e.stopPropagation(); setGapDetail(g); }}>
                      <Icon name="chevR" size={16} />
                    </Act>
                  </CaGap>
                );
              })}
            </div>
          )}
        </CaPad>

        <CaPad>
          <CaSecH>
            <div>
              <Icon name="users" size={16} />
              <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.needsAttention}>Needs attention</LabelWithHelp>
            </div>
            <Muted style={{ fontSize: theme.fsMicro }}>{atRisk.length} learner{atRisk.length === 1 ? '' : 's'}</Muted>
          </CaSecH>
          {atRisk.length === 0 ? (
            <CaEmpty>
              <KpiIco style={{ background: `color-mix(in oklch, ${theme.success} 14%, ${theme.surface})`, color: theme.success }}>
                <Icon name="check" />
              </KpiIco>
              <div>
                No learners are flagged.
                <br />
                <Muted style={{ fontSize: theme.fsMicro }}>Everyone is on pace.</Muted>
              </div>
            </CaEmpty>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {(showAllRisk ? atRisk : atRisk.slice(0, 5)).map((s, i) => (
                <CaRiskRow key={s.id ?? i}>
                  <Av style={{ background: theme.accent }}>{initials(s.name)}</Av>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <CellPrimary style={{ fontSize: 13 }}>{s.name}</CellPrimary>
                    <Muted style={{ fontSize: theme.fsMicro, marginTop: 2, display: 'block' }}>
                      {s.progress ?? 0}% complete · {s.reason || 'needs attention'}
                    </Muted>
                  </div>
                </CaRiskRow>
              ))}
              {atRisk.length > 5 && (
                <button
                  type="button"
                  onClick={() => setShowAllRisk((v) => !v)}
                  style={{
                    alignSelf: 'flex-start',
                    marginTop: 8,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: theme.accentStrong || theme.accent,
                    fontSize: theme.fsSm,
                    fontWeight: 600,
                  }}
                >
                  {showAllRisk ? 'Show less' : `Show all ${atRisk.length}`}
                </button>
              )}
            </div>
          )}
        </CaPad>
      </ChartGrid>

      <CaPad style={{ marginTop: theme.space }}>
        <CaSecH>
          <div>
            <Icon name="sparkles" size={16} />
            <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.yakiRecommends}>Yaki recommends</LabelWithHelp>
          </div>
          <Muted style={{ fontSize: theme.fsMicro }}>{insights.recs.length} actions · ordered by expected impact</Muted>
        </CaSecH>
        {insights.recs.length === 0 ? (
          <SectionEmpty>No recommendations for this course right now.</SectionEmpty>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {insights.recs.map((r, i) => (
              <CaRec key={i}>
                <CaRecIco><Icon name={r.ic} size={16} /></CaRecIco>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 650, fontSize: 14 }}>{r.title}</div>
                  <Muted style={{ fontSize: theme.fsSm, marginTop: 4, display: 'block', lineHeight: 1.5 }}>{r.body}</Muted>
                </div>
                <CaRecActs>
                  <Tag $tone="gray">{r.action}</Tag>
                </CaRecActs>
              </CaRec>
            ))}
          </div>
        )}
      </CaPad>

      <div style={{ marginTop: theme.space }}>
        <DataTable
          rows={sortedFlags}
          rowKey={(f, i) => f.id ?? i}
          sortBy={diagSortBy}
          sortDir={diagSortDir}
          onSortChange={handleDiagSortChange}
          onRowClick={(f) => setQDetail(f)}
          searchExtra={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="assess" size={16} style={{ color: theme.textMuted }} />
              <span style={{ fontWeight: 700, fontSize: theme.fsSm }}>
                <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.questionDiagnostics}>Question diagnostics</LabelWithHelp>
              </span>
              <Tag $tone="accent">{insights.flags.length} flagged</Tag>
            </div>
          }
          columns={[
            {
              header: <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.question}>Question</LabelWithHelp>,
              sortKey: COURSE_ANALYSIS_SORT_FIELDS.question,
              render: (f) => <CellPrimary>{f.q || f.text}</CellPrimary>,
            },
            {
              header: <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.difficulty}>Difficulty</LabelWithHelp>,
              sortKey: COURSE_ANALYSIS_SORT_FIELDS.difficulty,
              render: (f) => <Chip $soft>{f.diff || f.difficulty}</Chip>,
            },
            {
              header: <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.accuracy}>Accuracy</LabelWithHelp>,
              sortKey: COURSE_ANALYSIS_SORT_FIELDS.accuracy,
              render: (f) => {
                const acc = f.acc ?? f.accuracy ?? 0;
                return (
                  <MiniProg>
                    <div className="track"><i style={{ width: `${acc}%`, background: acc < 55 ? theme.danger : acc > 90 ? theme.warning : theme.accent }} /></div>
                    <span className="tnum">{acc}%</span>
                  </MiniProg>
                );
              },
            },
            {
              header: <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.discrimination}>Discrimination</LabelWithHelp>,
              sortKey: COURSE_ANALYSIS_SORT_FIELDS.discrimination,
              render: (f) => (
                <Tag $tone={f.disc === 'High' || f.discrimination === 'High' ? 'green' : (f.disc === 'Low' || f.discrimination === 'Low') ? 'red' : 'amber'}>
                  <TagDot />{f.disc || f.discrimination}
                </Tag>
              ),
            },
            {
              header: <LabelWithHelp help={COURSE_ANALYSIS_FIELD_HELP.yakiNote}>Yaki&apos;s note</LabelWithHelp>,
              sortKey: COURSE_ANALYSIS_SORT_FIELDS.note,
              muted: true,
              render: (f) => (f.flag || <span style={{ color: theme.textSubtle }}>—</span>),
            },
          ]}
          actions={(f) => (
            <>
              <Act title="View question" onClick={() => setQDetail(f)}><Icon name="eye" size={16} /></Act>
              {f.rewrite && (
                <Act title="View rewrite" onClick={() => setRewriting(f)}><Icon name="sparkles" size={16} /></Act>
              )}
            </>
          )}
          emptyText="No flagged questions for this course."
        />
      </div>

      {exporting && (
        <ExportAnalysisModal
          courseName={courseName}
          onClose={() => setExporting(false)}
        />
      )}
      {gapDetail && (
        <GapDetailModal
          gap={gapDetail}
          onClose={() => setGapDetail(null)}
          onOpenQuestion={(qid) => {
            const flag = findFlagById(qid);
            setGapDetail(null);
            if (flag) setQDetail(flag);
          }}
        />
      )}
      {qDetail && (
        <QuestionDetailModal
          flag={qDetail}
          onClose={() => setQDetail(null)}
          onRewrite={() => {
            guardAction('action.yaki.analysis.rewriteQuestion', () => {
              if (qDetail.rewrite) { setRewriting(qDetail); setQDetail(null); }
            });
          }}
        />
      )}
      {rewriting && (
        <RewriteModal
          flag={rewriting}
          onClose={() => setRewriting(null)}
        />
      )}
      {toast && (
        <Toast><Icon name="check" size={16} />{toast}</Toast>
      )}
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </Wrap>
  );
}
